import type { Exercise } from '../../types.ts';
export const batch4Reading: Exercise[] = [
  {
    id: 'reading-jevons',
    category: 'reading',
    title: 'Jevons paradox',
    topic: 'Jevons paradox',
    difficulty: 'Challenging',
    article: {
      title: 'Jevons paradox',
      url: 'https://en.wikipedia.org/wiki/Jevons_paradox',
      publisher: 'Wikipedia',
      reviewedOn: '2026-09-28',
      preview:
        'Efficiency changes the cost of using a technology—and sometimes changes how much people use it. This article distinguishes savings per task from total resource use, with examples and a debate about policy.',
      scope: {
        wordCount: 1568,
        sections: [
          {
            title: 'Introduction',
            url: 'https://en.wikipedia.org/wiki/Jevons_paradox',
            wordCount: 337,
            anchors: [''],
          },
          {
            title: 'History',
            url: 'https://en.wikipedia.org/wiki/Jevons_paradox#History',
            wordCount: 274,
            anchors: ['History'],
          },
          {
            title: 'Cause',
            url: 'https://en.wikipedia.org/wiki/Jevons_paradox#Cause',
            wordCount: 407,
            anchors: ['Cause', 'Conditions'],
          },
          {
            title: 'Energy conservation policy',
            url: 'https://en.wikipedia.org/wiki/Jevons_paradox#Energy_conservation_policy',
            wordCount: 550,
            anchors: ['Energy_conservation_policy'],
          },
        ],
      },
    },
    reading: {
      title: 'Jevons paradox — explanations',
      authorship:
        'Original questions and adapted factual explanations with source attribution; CC BY-SA 4.0.',
      sections: [
        {
          id: 'units',
          title: 'Per task and in total are different quantities',
          blocks: [
            {
              kind: 'paragraph',
              text: 'A device can become more efficient while the total resources consumed by all its users rise. Efficiency describes the input needed for a given output: for example, fuel per kilometer. Total use also depends on how many kilometers are traveled. Multiplying these two quantities keeps their different roles visible. A smaller input per task does not determine the product if the number of tasks changes.',
            },
            {
              kind: 'paragraph',
              text: 'In the Jevons account, cheaper useful output encourages additional applications and purchases. That response can offset some savings, all of them, or more than all. An increase in total use is therefore compatible with a real improvement in efficiency. It does not require the new machine to be less efficient than the old one or the measurements to be mistaken.',
            },
          ],
        },
        {
          id: 'rebound',
          title: 'An offset is not necessarily a reversal',
          blocks: [
            {
              kind: 'paragraph',
              text: 'Rebound describes additional use induced by efficiency-related cost reductions. Backfire, the Jevons case, is the stronger outcome in which the extra activity more than outweighs the resource saving. Suppose energy per trip falls from two units to one while trips rise from ten to fifteen. Use falls from twenty units to fifteen: there is some rebound, but consumption still decreases.',
            },
            {
              kind: 'paragraph',
              text: 'The hypothetical quiz changes both factors explicitly. Halving energy per task and multiplying tasks by 2.4 gives a total-use factor of 0.5 × 2.4 = 1.2. That is a twenty-percent increase over the original total. The source supplies the mechanism; the numbers are stated in the question so no outside empirical estimate is required.',
            },
          ],
        },
        {
          id: 'cost',
          title: 'Follow the effective price of the service',
          blocks: [
            {
              kind: 'paragraph',
              text: 'A resource price and the cost of the service produced from it are not the same thing. If a liter of fuel has the same price but a vehicle travels farther on it, fuel cost per kilometer has fallen. Demand can respond to that lower service cost without any fall in the price per liter. The historical steam-engine example similarly concerns useful power made cheaper by better coal efficiency.',
            },
            {
              kind: 'paragraph',
              text: 'How strongly quantity demanded responds matters. The article also notes that fuel is usually only one input among labor, equipment and other costs. A large reduction in fuel needed therefore need not produce an equally large reduction in the final service price. This is one reason an engineering efficiency figure alone cannot establish the size of a rebound in a real economy.',
            },
          ],
        },
        {
          id: 'limits',
          title: 'A possible mechanism is not a universal forecast',
          blocks: [
            {
              kind: 'paragraph',
              text: 'The article describes disagreement about how large rebounds are and how widely full backfire occurs. Identifying a causal pathway is different from measuring its strength in a particular market. A market with little additional demand may still conserve resources after efficiency improves. Other markets may expand substantially. Neither outcome follows from the word “efficiency” alone.',
            },
            {
              kind: 'paragraph',
              text: 'Policy discussion in the article includes pairing efficiency improvements with measures that constrain total use or keep the cost of use from falling as far. This is not the same as deliberately making machines wasteful. It separates the benefit of producing a task with fewer resources from the separate choice of how much aggregate consumption to permit or encourage.',
            },
          ],
        },
      ],
      sources: [
        {
          title: 'Jevons paradox · Wikipedia contributors',
          url: 'https://en.wikipedia.org/wiki/Jevons_paradox',
          note: 'Assigned external article; source-derived explanations and original questions in this set are provided under CC BY-SA 4.0.',
        },
        {
          title: 'CC BY-SA 4.0',
          url: 'https://creativecommons.org/licenses/by-sa/4.0/',
        },
      ],
    },
    questions: [
      {
        id: 'b4-jevons-1',
        prompt: 'What must happen for improved efficiency to produce the Jevons paradox?',
        options: [
          {
            id: '0',
            text: 'The same output must require more resource after the improvement.',
          },
          {
            id: '1',
            text: 'Resource prices must fall before the new technology is introduced.',
          },
          {
            id: '2',
            text: 'Expanded activity must more than offset the resource saving per task.',
          },
          {
            id: '3',
            text: 'The number of tasks must remain fixed while input per task falls.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '2',
        },
        explanation:
          'The paradox occurs when induced use outweighs efficiency savings, raising total consumption.',
        sectionIds: ['units'],
        evidence:
          'The paradox occurs when induced use outweighs efficiency savings, raising total consumption. Verified in assigned source section: Introduction.',
        sourceSection: {
          title: 'Wikipedia: Introduction',
          url: 'https://en.wikipedia.org/wiki/Jevons_paradox',
        },
      },
      {
        id: 'b4-jevons-2',
        prompt:
          'Energy per task halves while the number of tasks rises to 2.4 times its original level. With no other changes, what happens to total energy use?',
        options: [
          {
            id: '0',
            text: 'It rises by 20%.',
          },
          {
            id: '1',
            text: 'It falls by 50%.',
          },
          {
            id: '2',
            text: 'It rises by 140%.',
          },
          {
            id: '3',
            text: 'It falls by 20%.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '0',
        },
        explanation: 'Applying the assigned mechanism: 0.5 × 2.4 = 1.2 times original total use.',
        sectionIds: ['rebound'],
        evidence:
          'Applying the assigned mechanism: 0.5 × 2.4 = 1.2 times original total use. Verified in assigned source section: Cause.',
        sourceSection: {
          title: 'Wikipedia: Cause',
          url: 'https://en.wikipedia.org/wiki/Jevons_paradox#Cause',
        },
      },
      {
        id: 'b4-jevons-3',
        prompt:
          'Which change could lower the cost of driving even if the fuel price per liter stays unchanged?',
        options: [
          {
            id: '0',
            text: 'A proportional increase in fuel used per kilometer',
          },
          {
            id: '1',
            text: 'A decrease in the number of kilometers obtainable per liter',
          },
          {
            id: '2',
            text: 'A shift from fuel costs to an equally large fixed charge per kilometer',
          },
          {
            id: '3',
            text: 'An increase in the kilometers obtainable from each liter',
          },
        ],
        answer: {
          kind: 'choice',
          value: '3',
        },
        explanation:
          'The article distinguishes fuel price from effective travel cost; greater efficiency lowers fuel cost per kilometer.',
        sectionIds: ['cost'],
        evidence:
          'The article distinguishes fuel price from effective travel cost; greater efficiency lowers fuel cost per kilometer. Verified in assigned source section: Cause.',
        sourceSection: {
          title: 'Wikipedia: Cause',
          url: 'https://en.wikipedia.org/wiki/Jevons_paradox#Cause',
        },
      },
      {
        id: 'b4-jevons-4',
        prompt:
          'A study finds extra use after an efficiency improvement, but overall resource consumption still falls. How does the article distinguish this from full backfire?',
        options: [
          {
            id: '0',
            text: 'Any extra use counts as a total-consumption increase.',
          },
          {
            id: '1',
            text: 'There is rebound, but it does not fully offset the savings.',
          },
          {
            id: '2',
            text: 'The machines cannot have become more efficient.',
          },
          {
            id: '3',
            text: 'Demand must have been completely unresponsive to cost.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '1',
        },
        explanation:
          'Rebound may partially offset savings without exceeding them; full backfire requires an increase in total use.',
        sectionIds: ['rebound'],
        evidence:
          'Rebound may partially offset savings without exceeding them; full backfire requires an increase in total use. Verified in assigned source section: Cause.',
        sourceSection: {
          title: 'Wikipedia: Cause',
          url: 'https://en.wikipedia.org/wiki/Jevons_paradox#Cause',
        },
      },
      {
        id: 'b4-jevons-5',
        prompt:
          'Which policy approach discussed in the article addresses the rebound mechanism while retaining efficiency gains?',
        options: [
          {
            id: '0',
            text: 'Subsidize each additional unit of resource consumed.',
          },
          {
            id: '1',
            text: 'Assume service demand stays fixed after costs fall.',
          },
          {
            id: '2',
            text: 'Pair efficiency improvements with limits or charges on resource use.',
          },
          {
            id: '3',
            text: 'Judge aggregate savings solely from a machine’s input per task.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '2',
        },
        explanation:
          'The policy section discusses taxes and resource-use constraints alongside efficiency rather than treating efficiency alone as sufficient.',
        sectionIds: ['limits'],
        evidence:
          'The policy section discusses taxes and resource-use constraints alongside efficiency rather than treating efficiency alone as sufficient. Verified in assigned source section: Energy_conservation_policy.',
        sourceSection: {
          title: 'Wikipedia: Energy conservation policy',
          url: 'https://en.wikipedia.org/wiki/Jevons_paradox#Energy_conservation_policy',
        },
      },
    ],
  },
  {
    id: 'reading-encrypt',
    category: 'reading',
    title: "Let's Encrypt",
    topic: "Let's Encrypt",
    difficulty: 'Challenging',
    article: {
      title: "Let's Encrypt",
      url: 'https://en.wikipedia.org/wiki/Let%27s_Encrypt',
      publisher: 'Wikipedia',
      reviewedOn: '2026-09-28',
      preview:
        'A nonprofit helped make encrypted websites ordinary by automating a process that used to involve payment and manual administration. Read about what its certificates establish and how automated validation works.',
      scope: {
        wordCount: 962,
        sections: [
          {
            title: 'Introduction',
            url: 'https://en.wikipedia.org/wiki/Let%27s_Encrypt',
            wordCount: 119,
            anchors: [''],
          },
          {
            title: 'Overview',
            url: 'https://en.wikipedia.org/wiki/Let%27s_Encrypt#Overview',
            wordCount: 337,
            anchors: ['Overview'],
          },
          {
            title: 'ACME protocol',
            url: 'https://en.wikipedia.org/wiki/Let%27s_Encrypt#ACME_protocol',
            wordCount: 275,
            anchors: ['ACME_protocol'],
          },
          {
            title: 'Software implementation',
            url: 'https://en.wikipedia.org/wiki/Let%27s_Encrypt#Software_implementation',
            wordCount: 231,
            anchors: ['Software_implementation'],
          },
        ],
      },
    },
    reading: {
      title: "Let's Encrypt — explanations",
      authorship:
        'Original questions and adapted factual explanations with source attribution; CC BY-SA 4.0.',
      sections: [
        {
          id: 'purpose',
          title: 'Reduce both price and administrative work',
          blocks: [
            {
              kind: 'paragraph',
              text: 'Let’s Encrypt issues certificates used to establish TLS-encrypted web connections. Its public-benefit goal is wider use of HTTPS. Removing certificate fees addresses one barrier, but the article gives automation an equally important role: software can request, validate, install and renew certificates. A free certificate that still demanded frequent manual work would leave a different adoption obstacle in place.',
            },
            {
              kind: 'paragraph',
              text: 'The service is a certificate authority, not the operator of every site receiving a certificate. Its function is part of the infrastructure enabling secure connections. The quiz asks about that institutional role, rather than changing counts of supported websites, sponsor lists or a particular certificate lifetime. Those details can evolve without changing the basic mechanism.',
            },
          ],
        },
        {
          id: 'validation',
          title: 'Control of a domain is a specific claim',
          blocks: [
            {
              kind: 'paragraph',
              text: 'A domain-validated certificate establishes the applicant’s control of the named domain under the authority’s validation procedure. It does not perform the human organizational checks associated with organization-validated or extended-validation certificates. The assigned Overview explicitly connects this narrower scope with the possibility of fully automated issuance.',
            },
            {
              kind: 'paragraph',
              text: 'This distinction matters when interpreting a successful check. Showing that software can answer a challenge at a domain is evidence about control of that domain, not an audit of the operator’s business practices. The result should be interpreted at the level actually tested. Treating one successful technical check as a broader endorsement would extend the evidence beyond its scope.',
            },
          ],
        },
        {
          id: 'challenge',
          title: 'A challenge must be answered through the controlled resource',
          blocks: [
            {
              kind: 'paragraph',
              text: 'ACME is the protocol connecting an applicant’s client software to a certificate authority. The authority asks for an expected response through a resource associated with the requested domain, such as a web endpoint or DNS record. An applicant who controls that resource can arrange the response; merely typing another person’s domain name into a form cannot satisfy the same check.',
            },
            {
              kind: 'paragraph',
              text: 'The article also describes validation over multiple network paths and locations. This makes an attack that deceives one network vantage point less sufficient. It is a defense against a particular class of manipulation, not proof that every possible attack has been eliminated. The local explanation describes the principle; it is not installation guidance for the historical protocol details mentioned in the article.',
            },
          ],
        },
        {
          id: 'roles',
          title: 'Separate the client from the authority',
          blocks: [
            {
              kind: 'paragraph',
              text: 'The article names Boulder as software on the certificate-authority side and Certbot as a client that can run for the website operator. They participate in the same workflow but have different responsibilities. A client requests and helps install or renew a certificate; the authority validates the request and issues the certificate under its policies.',
            },
            {
              kind: 'paragraph',
              text: 'Automation also has a maintenance role. A certificate does not remain valid indefinitely, so a working initial installation is not the end of the process. A client capable of renewal can repeat the required work before expiration. The existence of multiple ACME clients means the protocol is not simply another name for one particular client application.',
            },
          ],
        },
      ],
      sources: [
        {
          title: "Let's Encrypt · Wikipedia contributors",
          url: 'https://en.wikipedia.org/wiki/Let%27s_Encrypt',
          note: 'Assigned external article; source-derived explanations and original questions in this set are provided under CC BY-SA 4.0.',
        },
        {
          title: 'CC BY-SA 4.0',
          url: 'https://creativecommons.org/licenses/by-sa/4.0/',
        },
      ],
    },
    questions: [
      {
        id: 'b4-encrypt-1',
        prompt: 'Which pair of barriers does the article say Let’s Encrypt tries to reduce?',
        options: [
          {
            id: '0',
            text: 'Certificate fees and manual certificate administration',
          },
          {
            id: '1',
            text: 'The need for a domain name and the need for a web server',
          },
          {
            id: '2',
            text: 'All software vulnerabilities and all malicious website content',
          },
          {
            id: '3',
            text: 'Internet access charges and the cost of creating website text',
          },
        ],
        answer: {
          kind: 'choice',
          value: '0',
        },
        explanation:
          'The mission combines no-fee issuance with automation of validation, installation and renewal.',
        sectionIds: ['purpose'],
        evidence:
          'The mission combines no-fee issuance with automation of validation, installation and renewal. Verified in assigned source section: Overview.',
        sourceSection: {
          title: 'Wikipedia: Overview',
          url: 'https://en.wikipedia.org/wiki/Let%27s_Encrypt#Overview',
        },
      },
      {
        id: 'b4-encrypt-2',
        prompt: 'Why does the article connect domain validation with fully automated issuance?',
        options: [
          {
            id: '0',
            text: 'It verifies the operator’s entire corporate history.',
          },
          {
            id: '1',
            text: 'It requires every applicant to attend an identity interview.',
          },
          {
            id: '2',
            text: 'It avoids having to test control of the domain.',
          },
          {
            id: '3',
            text: 'It can verify domain control without the human organizational checks of OV or EV validation.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '3',
        },
        explanation:
          'Overview says only domain-validated certificates are offered because they can be fully automated.',
        sectionIds: ['validation'],
        evidence:
          'Overview says only domain-validated certificates are offered because they can be fully automated. Verified in assigned source section: Overview.',
        sourceSection: {
          title: 'Wikipedia: Overview',
          url: 'https://en.wikipedia.org/wiki/Let%27s_Encrypt#Overview',
        },
      },
      {
        id: 'b4-encrypt-3',
        prompt:
          'In the ACME process described, what is the purpose of placing an expected response on a web or DNS resource?',
        options: [
          {
            id: '0',
            text: 'To transfer permanent domain ownership to the authority',
          },
          {
            id: '1',
            text: 'To demonstrate control of a resource associated with the domain',
          },
          {
            id: '2',
            text: 'To publish the website’s private encryption key',
          },
          {
            id: '3',
            text: 'To establish that every statement on the website is accurate',
          },
        ],
        answer: {
          kind: 'choice',
          value: '1',
        },
        explanation:
          'ACME challenge responses demonstrate control through the web or DNS resources associated with the domain.',
        sectionIds: ['challenge'],
        evidence:
          'ACME challenge responses demonstrate control through the web or DNS resources associated with the domain. Verified in assigned source section: ACME_protocol.',
        sourceSection: {
          title: 'Wikipedia: ACME protocol',
          url: 'https://en.wikipedia.org/wiki/Let%27s_Encrypt#ACME_protocol',
        },
      },
      {
        id: 'b4-encrypt-4',
        prompt: 'Why perform domain-validation checks from geographically diverse locations?',
        options: [
          {
            id: '0',
            text: 'To translate the certificate into the local language of each visitor',
          },
          {
            id: '1',
            text: 'To replace the client’s renewal schedule with a global clock',
          },
          {
            id: '2',
            text: 'To make localized network deception less sufficient to pass validation',
          },
          {
            id: '3',
            text: 'To make the domain belong jointly to several countries',
          },
        ],
        answer: {
          kind: 'choice',
          value: '2',
        },
        explanation:
          'The source describes diverse validation locations as making DNS spoofing harder.',
        sectionIds: ['challenge'],
        evidence:
          'The source describes diverse validation locations as making DNS spoofing harder. Verified in assigned source section: ACME_protocol.',
        sourceSection: {
          title: 'Wikipedia: ACME protocol',
          url: 'https://en.wikipedia.org/wiki/Let%27s_Encrypt#ACME_protocol',
        },
      },
      {
        id: 'b4-encrypt-5',
        prompt: 'Which distinction between Boulder and Certbot matches the article?',
        options: [
          {
            id: '0',
            text: 'Boulder implements the authority side; Certbot is client-side certificate-management software.',
          },
          {
            id: '1',
            text: 'Boulder edits website pages; Certbot is the certificate authority’s legal name.',
          },
          {
            id: '2',
            text: 'Boulder only renews certificates; Certbot only approves initial domain ownership.',
          },
          {
            id: '3',
            text: 'Boulder and Certbot are two names for the same required application.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '0',
        },
        explanation:
          'The implementation section explicitly distinguishes the server-side authority software from the client.',
        sectionIds: ['roles'],
        evidence:
          'The implementation section explicitly distinguishes the server-side authority software from the client. Verified in assigned source section: Software_implementation.',
        sourceSection: {
          title: 'Wikipedia: Software implementation',
          url: 'https://en.wikipedia.org/wiki/Let%27s_Encrypt#Software_implementation',
        },
      },
    ],
  },
  {
    id: 'reading-habakkuk',
    category: 'reading',
    title: 'Project Habakkuk',
    topic: 'Project Habakkuk',
    difficulty: 'Challenging',
    article: {
      title: 'Project Habakkuk',
      url: 'https://en.wikipedia.org/wiki/Project_Habakkuk',
      publisher: 'Wikipedia',
      reviewedOn: '2026-09-28',
      preview:
        'An aircraft carrier made partly of ice reached the prototype stage during the Second World War. The assigned sections explain the material, the military problem, and why a promising demonstration was not enough.',
      scope: {
        wordCount: 1678,
        sections: [
          {
            title: 'Introduction',
            url: 'https://en.wikipedia.org/wiki/Project_Habakkuk',
            wordCount: 164,
            anchors: [''],
          },
          {
            title: 'Initial concept',
            url: 'https://en.wikipedia.org/wiki/Project_Habakkuk#Initial_concept',
            wordCount: 311,
            anchors: ['Initial_concept'],
          },
          {
            title: 'Pykrete',
            url: 'https://en.wikipedia.org/wiki/Project_Habakkuk#Pykrete',
            wordCount: 268,
            anchors: ['Pykrete'],
          },
          {
            title: 'Scale model',
            url: 'https://en.wikipedia.org/wiki/Project_Habakkuk#Scale_model',
            wordCount: 566,
            anchors: ['Scale_model'],
          },
          {
            title: 'End of project',
            url: 'https://en.wikipedia.org/wiki/Project_Habakkuk#End_of_project',
            wordCount: 369,
            anchors: ['End_of_project'],
          },
        ],
      },
    },
    reading: {
      title: 'Project Habakkuk — explanations',
      authorship:
        'Original questions and adapted factual explanations with source attribution; CC BY-SA 4.0.',
      sections: [
        {
          id: 'mission',
          title: 'Start with the gap the proposal tried to fill',
          blocks: [
            {
              kind: 'paragraph',
              text: 'The proposal addressed stretches of the Atlantic beyond the useful reach of land-based air cover. A floating base could let aircraft protect convoys in that gap. Steel and other materials were also in demand for wartime production, making an unconventional structural material attractive. These were linked operational and resource constraints, not a desire to build an unusual ship for its own sake.',
            },
            {
              kind: 'paragraph',
              text: 'The relevant comparison changes when other ways of providing air cover improve. A design can become less useful even if its material still has interesting properties. Longer-range aircraft, access to additional airfields and more escort carriers reduced the need that had originally justified the large floating base. The quiz asks about that changing comparison, not an exact prototype date.',
            },
          ],
        },
        {
          id: 'material',
          title: 'A composite improves some properties, not every requirement',
          blocks: [
            {
              kind: 'paragraph',
              text: 'Pykrete combines water with wood pulp and is used in frozen form. In the assigned account it is stronger and melts more slowly than plain ice, and can be shaped by machining or casting. Those properties make the proposal more plausible than simply flattening a natural iceberg, which has limited freeboard and can roll.',
            },
            {
              kind: 'paragraph',
              text: 'However, resistance to breaking is not the same as resistance to gradual deformation. Perutz found that the material could slowly sag through plastic flow. The project therefore needed insulation and refrigeration to keep the structure sufficiently cold. “Made of ice” did not mean it could be placed in seawater indefinitely without supporting machinery or maintenance.',
            },
          ],
        },
        {
          id: 'scale',
          title: 'A material demonstration does not settle the whole design',
          blocks: [
            {
              kind: 'paragraph',
              text: 'A prototype can answer a narrow question, such as whether insulation and refrigeration preserve a structure under test conditions. A full carrier adds requirements about range, waves, torpedoes, aircraft takeoff and steering. The article describes growing requirements that demanded a larger deck, thicker hull and additional reinforcement. These changes increased the production burden.',
            },
            {
              kind: 'paragraph',
              text: 'The unresolved rudder problem illustrates why success with a material cannot be treated as success with every subsystem. Engineering evaluation asks whether the complete design satisfies its operating requirements at an acceptable cost. The scale-model evidence was useful, but it did not logically guarantee that a much larger ocean-going vessel would be practical.',
            },
          ],
        },
        {
          id: 'alternatives',
          title: 'Compare complete ways of achieving the purpose',
          blocks: [
            {
              kind: 'paragraph',
              text: 'The project’s cancellation reflected technical and resource demands alongside alternatives that were reducing the Atlantic air-coverage gap. More escort carriers, additional airfield access and improved aircraft range changed the value of proceeding. This does not mean that all experiments were failures or that pykrete had no unusual properties.',
            },
            {
              kind: 'paragraph',
              text: 'The article contains inconsistent-looking prototype chronology and multiple accounts of costs. The questions deliberately avoid requiring those details. The supported comparison is broader: an expensive, technically demanding proposed platform became less compelling as other methods served its intended purpose. Evaluating the whole system gives a more useful explanation than treating one impressive material test as a decisive verdict.',
            },
          ],
        },
      ],
      sources: [
        {
          title: 'Project Habakkuk · Wikipedia contributors',
          url: 'https://en.wikipedia.org/wiki/Project_Habakkuk',
          note: 'Assigned external article; source-derived explanations and original questions in this set are provided under CC BY-SA 4.0.',
        },
        {
          title: 'CC BY-SA 4.0',
          url: 'https://creativecommons.org/licenses/by-sa/4.0/',
        },
      ],
    },
    questions: [
      {
        id: 'b4-habakkuk-1',
        prompt: 'What operational problem was Project Habakkuk intended to address?',
        options: [
          {
            id: '0',
            text: 'The lack of inland runways for transporting troops by rail',
          },
          {
            id: '1',
            text: 'The need to store aircraft permanently below sea level',
          },
          {
            id: '2',
            text: 'A mid-Atlantic gap in air protection for shipping',
          },
          {
            id: '3',
            text: 'The inability of aircraft to operate from any conventional carrier',
          },
        ],
        answer: {
          kind: 'choice',
          value: '2',
        },
        explanation:
          'The introduction and initial concept describe convoy protection beyond land-based aircraft range.',
        sectionIds: ['mission'],
        evidence:
          'The introduction and initial concept describe convoy protection beyond land-based aircraft range. Verified in assigned source section: Introduction.',
        sourceSection: {
          title: 'Wikipedia: Introduction',
          url: 'https://en.wikipedia.org/wiki/Project_Habakkuk',
        },
      },
      {
        id: 'b4-habakkuk-2',
        prompt: 'What distinguished pykrete from plain ice in the assigned account?',
        options: [
          {
            id: '0',
            text: 'It was frozen wood pulp and water with greater strength and slower melting.',
          },
          {
            id: '1',
            text: 'It was seawater purified until it needed no cooling.',
          },
          {
            id: '2',
            text: 'It was a metal shell with ice used only as ballast.',
          },
          {
            id: '3',
            text: 'It was ordinary ice kept strong solely by paint.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '0',
        },
        explanation:
          'Pykrete was a frozen wood-pulp/water composite with improved strength and melting behavior.',
        sectionIds: ['material'],
        evidence:
          'Pykrete was a frozen wood-pulp/water composite with improved strength and melting behavior. Verified in assigned source section: Pykrete.',
        sourceSection: {
          title: 'Wikipedia: Pykrete',
          url: 'https://en.wikipedia.org/wiki/Project_Habakkuk#Pykrete',
        },
      },
      {
        id: 'b4-habakkuk-3',
        prompt: 'Why was a refrigeration system still required despite pykrete’s strength?',
        options: [
          {
            id: '0',
            text: 'To generate lift for aircraft taking off',
          },
          {
            id: '1',
            text: 'To turn wood pulp into steel reinforcement',
          },
          {
            id: '2',
            text: 'To prevent the hull from becoming more buoyant',
          },
          {
            id: '3',
            text: 'To limit slow deformation of the ice-based structure',
          },
        ],
        answer: {
          kind: 'choice',
          value: '3',
        },
        explanation:
          'Perutz identified plastic flow and sagging, requiring sufficiently low temperature.',
        sectionIds: ['material'],
        evidence:
          'Perutz identified plastic flow and sagging, requiring sufficiently low temperature. Verified in assigned source section: Pykrete.',
        sourceSection: {
          title: 'Wikipedia: Pykrete',
          url: 'https://en.wikipedia.org/wiki/Project_Habakkuk#Pykrete',
        },
      },
      {
        id: 'b4-habakkuk-4',
        prompt: 'Which lesson follows from the scale-model discussion?',
        options: [
          {
            id: '0',
            text: 'A successful prototype removed the need to solve steering.',
          },
          {
            id: '1',
            text: 'Material performance did not by itself establish full-vessel practicality.',
          },
          {
            id: '2',
            text: 'The full carrier had fewer requirements than the prototype.',
          },
          {
            id: '3',
            text: 'Once a material is strong, hull dimensions no longer affect cost.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '1',
        },
        explanation:
          'The section describes rising requirements, reinforcement/insulation costs and unresolved steering alongside prototype tests.',
        sectionIds: ['scale'],
        evidence:
          'The section describes rising requirements, reinforcement/insulation costs and unresolved steering alongside prototype tests. Verified in assigned source section: Scale_model.',
        sourceSection: {
          title: 'Wikipedia: Scale model',
          url: 'https://en.wikipedia.org/wiki/Project_Habakkuk#Scale_model',
        },
      },
      {
        id: 'b4-habakkuk-5',
        prompt:
          'Which development weakened the strategic case for the project even apart from its engineering difficulties?',
        options: [
          {
            id: '0',
            text: 'A reduced need for Atlantic convoy protection because shipping ended',
          },
          {
            id: '1',
            text: 'A ban on using aircraft for naval patrols',
          },
          {
            id: '2',
            text: 'Other aircraft bases and carriers increasingly provided the missing air cover',
          },
          {
            id: '3',
            text: 'The disappearance of the underlying ice-flow problem',
          },
        ],
        answer: {
          kind: 'choice',
          value: '2',
        },
        explanation:
          'The end-of-project section lists Azores access, longer-range patrol aircraft and more escort carriers.',
        sectionIds: ['alternatives'],
        evidence:
          'The end-of-project section lists Azores access, longer-range patrol aircraft and more escort carriers. Verified in assigned source section: End_of_project.',
        sourceSection: {
          title: 'Wikipedia: End of project',
          url: 'https://en.wikipedia.org/wiki/Project_Habakkuk#End_of_project',
        },
      },
    ],
  },
  {
    id: 'reading-dabbawala',
    category: 'reading',
    title: 'Dabbawala',
    topic: 'Dabbawala',
    difficulty: 'Challenging',
    article: {
      title: 'Dabbawala',
      url: 'https://en.wikipedia.org/wiki/Dabbawala',
      publisher: 'Wikipedia',
      reviewedOn: '2026-09-28',
      preview:
        'Mumbai’s lunchbox network moves meals through collectors, railway sorting and local delivery, then returns the containers. The article also examines a famous reliability claim and how it became folklore.',
      scope: {
        wordCount: 1234,
        sections: [
          {
            title: 'Introduction',
            url: 'https://en.wikipedia.org/wiki/Dabbawala',
            wordCount: 74,
            anchors: [''],
          },
          {
            title: 'Origins',
            url: 'https://en.wikipedia.org/wiki/Dabbawala#Origins',
            wordCount: 136,
            anchors: ['Origins'],
          },
          {
            title: 'Etymology',
            url: 'https://en.wikipedia.org/wiki/Dabbawala#Etymology',
            wordCount: 52,
            anchors: ['Etymology'],
          },
          {
            title: 'Colour-coding system',
            url: 'https://en.wikipedia.org/wiki/Dabbawala#Colour-coding_system',
            wordCount: 278,
            anchors: ['Colour-coding_system'],
          },
          {
            title: 'Association',
            url: 'https://en.wikipedia.org/wiki/Dabbawala#Association',
            wordCount: 136,
            anchors: ['Association'],
          },
          {
            title: 'Economic analysis',
            url: 'https://en.wikipedia.org/wiki/Dabbawala#Economic_analysis',
            wordCount: 431,
            anchors: ['Economic_analysis'],
          },
          {
            title: 'Studies',
            url: 'https://en.wikipedia.org/wiki/Dabbawala#Studies',
            wordCount: 127,
            anchors: ['Studies'],
          },
        ],
      },
    },
    reading: {
      title: 'Dabbawala — explanations',
      authorship:
        'Original questions and adapted factual explanations with source attribution; CC BY-SA 4.0.',
      sections: [
        {
          id: 'service',
          title: 'The network moves a meal chosen elsewhere',
          blocks: [
            {
              kind: 'paragraph',
              text: 'The service described is a delivery-and-return network linking homes or meal makers with people at work. Its historical appeal included access to food matching customers’ household and community preferences when workplace alternatives were limited. Delivering a lunch prepared elsewhere is a different business function from standardizing the menu in a single central kitchen.',
            },
            {
              kind: 'paragraph',
              text: 'The return trip is part of the system, not an incidental extra. Containers are collected after use and routed back so they can be used again. A description that ends with handing a full box to an office worker misses this reverse flow. The article’s historical wage and volume figures are dated; the quiz uses the operating relationships instead of treating those numbers as current.',
            },
          ],
        },
        {
          id: 'routing',
          title: 'Marks let different workers act on the same package',
          blocks: [
            {
              kind: 'paragraph',
              text: 'The markings identify stages and destinations such as collection points, railway stations, buildings and floors. A box passes through several workers, so its routing information must travel with it. Collectors bring boxes to sorting points, rail transport moves grouped boxes, and local deliverers handle the final part. This distributes work without requiring one worker to escort every meal all the way.',
            },
            {
              kind: 'paragraph',
              text: 'The value of a routing code is its shared interpretation within the system. It need not be a complete ordinary-language address to guide a trained sorter. The assigned account also describes capacity constraints: a new transport option is not automatically useful if its cabins cannot accommodate the required volume of lunchboxes. Coordination depends on fit with the physical network as well as the presence of modern equipment.',
            },
          ],
        },
        {
          id: 'organization',
          title: 'Coordination includes institutions as well as labels',
          blocks: [
            {
              kind: 'paragraph',
              text: 'A delivery system with multiple workers needs ways to address disputes and shared problems. The association described in the article provides a collective forum, including help with legal matters and conflicts. This is a different role from the destination codes printed or painted on an individual lunchbox. One manages relationships among people; the other directs an object through the network.',
            },
            {
              kind: 'paragraph',
              text: 'It is therefore incomplete to explain reliability solely as a clever marking system. Sorting conventions, established transport routes, repeated handoffs and organizational arrangements all contribute to how the service operates. This does not require assuming that every worker performs the same task or that one centralized employee personally makes every delivery decision.',
            },
          ],
        },
        {
          id: 'evidence',
          title: 'An estimate and a certification are different evidence',
          blocks: [
            {
              kind: 'paragraph',
              text: 'The article traces the extraordinary error-rate claim to an estimate supplied by an association representative and extrapolated by a journalist. It explicitly distinguishes that story from a rigorous measurement study and rejects the recurring claim that Forbes formally certified the organization as Six Sigma. Repetition of a number across many articles does not create new independent measurements.',
            },
            {
              kind: 'paragraph',
              text: 'This limitation does not establish that the system is unreliable. It establishes that the precision and certification attached to one popular claim are unsupported in the way often reported. A careful conclusion can recognize an impressive coordinated service while asking how errors were counted, over what interval, and with what definition. Studies listed in the article do not retroactively turn the original estimate into a formal certification.',
            },
          ],
        },
      ],
      sources: [
        {
          title: 'Dabbawala · Wikipedia contributors',
          url: 'https://en.wikipedia.org/wiki/Dabbawala',
          note: 'Assigned external article; source-derived explanations and original questions in this set are provided under CC BY-SA 4.0.',
        },
        {
          title: 'CC BY-SA 4.0',
          url: 'https://creativecommons.org/licenses/by-sa/4.0/',
        },
      ],
    },
    questions: [
      {
        id: 'b4-dabbawala-1',
        prompt: 'Which description captures the service’s basic operating cycle?',
        options: [
          {
            id: '0',
            text: 'Prepare a common menu at a central depot and send it directly to offices.',
          },
          {
            id: '1',
            text: 'Move meals from homes or meal makers to workers and route the empty containers back.',
          },
          {
            id: '2',
            text: 'Assign one collector to accompany each meal throughout its entire outward and return journey.',
          },
          {
            id: '3',
            text: 'Deliver home-prepared meals outward, leaving container returns entirely to customers.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '1',
        },
        explanation:
          'The introduction and routing section describe delivery from homes or makers and return of empty boxes.',
        sectionIds: ['service'],
        evidence:
          'The introduction and routing section describe delivery from homes or makers and return of empty boxes. Verified in assigned source section: Introduction.',
        sourceSection: {
          title: 'Wikipedia: Introduction',
          url: 'https://en.wikipedia.org/wiki/Dabbawala',
        },
      },
      {
        id: 'b4-dabbawala-2',
        prompt: 'Why does the coding system contain station and building information?',
        options: [
          {
            id: '0',
            text: 'The marks group meals by menu so matching food can be delivered together.',
          },
          {
            id: '1',
            text: 'Each code names the single worker who must perform the entire trip.',
          },
          {
            id: '2',
            text: 'The marks eliminate the need for sorting at railway stations.',
          },
          {
            id: '3',
            text: 'Successive workers need routing information for different stages of the journey.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '3',
        },
        explanation:
          'Codes support sorting by station and final building/floor through a sequence of handoffs.',
        sectionIds: ['routing'],
        evidence:
          'Codes support sorting by station and final building/floor through a sequence of handoffs. Verified in assigned source section: Colour-coding_system.',
        sourceSection: {
          title: 'Wikipedia: Colour-coding system',
          url: 'https://en.wikipedia.org/wiki/Dabbawala#Colour-coding_system',
        },
      },
      {
        id: 'b4-dabbawala-3',
        prompt:
          'Why might a newer rail service described in the article still be unsuitable for the network?',
        options: [
          {
            id: '0',
            text: 'Its cabins may lack the capacity for the lunchbox loads.',
          },
          {
            id: '1',
            text: 'Its greater speed prevents boxes from being marked before boarding.',
          },
          {
            id: '2',
            text: 'Its electrical power is incompatible with the metal lunch containers.',
          },
          {
            id: '3',
            text: 'Its routes have been rejected because workers use bicycles for the whole journey.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '0',
        },
        explanation:
          'The routing section explicitly cites inadequate cabin capacity for hundreds of tiffins as a reason some new infrastructure is unused.',
        sectionIds: ['routing'],
        evidence:
          'The routing section explicitly cites inadequate cabin capacity for hundreds of tiffins as a reason some new infrastructure is unused. Verified in assigned source section: Colour-coding_system.',
        sourceSection: {
          title: 'Wikipedia: Colour-coding system',
          url: 'https://en.wikipedia.org/wiki/Dabbawala#Colour-coding_system',
        },
      },
      {
        id: 'b4-dabbawala-4',
        prompt: 'What role does the association play beyond physically moving boxes?',
        options: [
          {
            id: '0',
            text: 'Setting a common menu for households preparing the meals',
          },
          {
            id: '1',
            text: 'Assigning a single worker to accompany each box through every stage',
          },
          {
            id: '2',
            text: 'Handling collective difficulties and disputes among participants',
          },
          {
            id: '3',
            text: 'Replacing local sorting decisions with centralized box-by-box routing',
          },
        ],
        answer: {
          kind: 'choice',
          value: '2',
        },
        explanation:
          'The association section describes a united front and assistance with conflicts and legal issues.',
        sectionIds: ['organization'],
        evidence:
          'The association section describes a united front and assistance with conflicts and legal issues. Verified in assigned source section: Association.',
        sourceSection: {
          title: 'Wikipedia: Association',
          url: 'https://en.wikipedia.org/wiki/Dabbawala#Association',
        },
      },
      {
        id: 'b4-dabbawala-5',
        prompt:
          'What does the article support about the often-repeated exceptionally low error rate?',
        options: [
          {
            id: '0',
            text: 'It came from a formal Forbes Six Sigma certification.',
          },
          {
            id: '1',
            text: 'It was extrapolated from an estimate, rather than established by the rigorous study often implied.',
          },
          {
            id: '2',
            text: 'It was calculated from a complete published log of failed deliveries.',
          },
          {
            id: '3',
            text: 'It was directly measured in the later Harvard case study described.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '1',
        },
        explanation:
          'Economic analysis distinguishes the original estimate and journalistic extrapolation from formal certification or rigorous measurement.',
        sectionIds: ['evidence'],
        evidence:
          'Economic analysis distinguishes the original estimate and journalistic extrapolation from formal certification or rigorous measurement. Verified in assigned source section: Economic_analysis.',
        sourceSection: {
          title: 'Wikipedia: Economic analysis',
          url: 'https://en.wikipedia.org/wiki/Dabbawala#Economic_analysis',
        },
      },
    ],
  },
  {
    id: 'reading-great-stink',
    category: 'reading',
    title: 'Great Stink',
    topic: 'Great Stink',
    difficulty: 'Challenging',
    article: {
      title: 'Great Stink',
      url: 'https://en.wikipedia.org/wiki/Great_Stink',
      publisher: 'Wikipedia',
      reviewedOn: '2026-09-28',
      preview:
        'A summer smell helped break a political deadlock over London’s sewers. Read how household improvements, a mistaken disease theory and contested public works combined to reshape the city.',
      scope: {
        wordCount: 2350,
        sections: [
          {
            title: 'Introduction',
            url: 'https://en.wikipedia.org/wiki/Great_Stink',
            wordCount: 313,
            anchors: [''],
          },
          {
            title: 'Background',
            url: 'https://en.wikipedia.org/wiki/Great_Stink#Background',
            wordCount: 584,
            anchors: ['Background'],
          },
          {
            title: 'Local government',
            url: 'https://en.wikipedia.org/wiki/Great_Stink#Local_government',
            wordCount: 629,
            anchors: ['Local_government'],
          },
          {
            title: 'June to August 1858',
            url: 'https://en.wikipedia.org/wiki/Great_Stink#June_to_August_1858',
            wordCount: 824,
            anchors: ['June_to_August_1858'],
          },
        ],
      },
    },
    reading: {
      title: 'Great Stink — explanations',
      authorship:
        'Original questions and adapted factual explanations with source attribution; CC BY-SA 4.0.',
      sections: [
        {
          id: 'systems',
          title: 'A household improvement can overload a shared outlet',
          blocks: [
            {
              kind: 'paragraph',
              text: 'Flushing toilets and better water supply improved parts of domestic life while sending more wastewater into London’s existing drainage network. Population growth and industrial discharges added further load. Because many outlets discharged to the Thames, moving waste away from individual houses did not remove it from the wider environment.',
            },
            {
              kind: 'paragraph',
              text: 'This is a boundary problem in evaluating an intervention. If the evaluation stops at the house, flushing looks like successful removal. If it includes the river used by the city, the same flow is an added input of contamination. The article’s description helps explain why a collection of local improvements can require a coordinated downstream infrastructure change rather than merely more flushing.',
            },
          ],
        },
        {
          id: 'disease',
          title: 'An effective intervention need not prove its motivating theory',
          blocks: [
            {
              kind: 'paragraph',
              text: 'The miasma theory attributed disease to contaminated air and smells. Snow instead argued for waterborne transmission of cholera. The assignment describes water-supply comparisons and the Broad Street investigation as part of that alternative explanation. Removing sewage from areas where people obtained water could help even when officials justified action by a mistaken account of how the disease traveled.',
            },
            {
              kind: 'paragraph',
              text: 'Consequently, a successful sanitation measure does not by itself validate every belief of the officials who supported it. Interventions can affect more than one pathway at once. The quiz distinguishes the historical belief about air from the waterborne mechanism, rather than requiring dates or death totals. It also does not treat the pump-handle episode alone as a controlled experiment establishing all causation.',
            },
          ],
        },
        {
          id: 'design',
          title: 'Collect locally and intercept at a larger scale',
          blocks: [
            {
              kind: 'paragraph',
              text: 'Bazalgette’s proposal connected smaller local sewers to larger main routes that carried waste eastward toward outfalls beyond the central city. High-, middle- and low-level areas needed coordinated drainage. Pumping stations helped move sewage where gravity alone was insufficient. An outfall is a discharge point, not a treatment process that makes waste vanish.',
            },
            {
              kind: 'paragraph',
              text: 'The location of those discharge points was a substantive dispute. Moving waste out of a neighborhood can leave the burden downstream, so agreeing that a sewer is needed does not settle where its contents should go. The proposed network was more than isolated repairs to individual household pipes; it changed how local collections connected to a citywide route.',
            },
          ],
        },
        {
          id: 'politics',
          title: 'A plan needs authority and a way to pay for it',
          blocks: [
            {
              kind: 'paragraph',
              text: 'The engineering proposals predated the worst summer smell, but jurisdiction, costs and discharge locations delayed action. When the stench interfered with Parliament’s own work, it sharpened pressure to resolve those disputes. The resulting legislation assigned responsibility to the Metropolitan Board of Works and enabled borrowing backed by a levy on households.',
            },
            {
              kind: 'paragraph',
              text: 'This joins a physical problem to an institutional response. An available technical drawing does not finance construction or settle which body can act. Conversely, treating curtains with chemicals could address an immediate symptom without providing the network needed for continuing waste flows. The article’s account supports a combination of engineering design, public authority and funding rather than a single sudden scientific discovery.',
            },
          ],
        },
      ],
      sources: [
        {
          title: 'Great Stink · Wikipedia contributors',
          url: 'https://en.wikipedia.org/wiki/Great_Stink',
          note: 'Assigned external article; source-derived explanations and original questions in this set are provided under CC BY-SA 4.0.',
        },
        {
          title: 'CC BY-SA 4.0',
          url: 'https://creativecommons.org/licenses/by-sa/4.0/',
        },
      ],
    },
    questions: [
      {
        id: 'b4-great-stink-1',
        prompt: 'Why did the spread of flushing toilets contribute to the river problem described?',
        options: [
          {
            id: '0',
            text: 'They removed the need for any connection between homes and sewers.',
          },
          {
            id: '1',
            text: 'They reduced wastewater flow enough to dry the riverbed.',
          },
          {
            id: '2',
            text: 'They increased the flow of waste through a network that discharged into the Thames.',
          },
          {
            id: '3',
            text: 'They replaced household waste with harmless drinking water before discharge.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '2',
        },
        explanation:
          'Background links improved water supply and flushing toilets to increased sewage flows into the overloaded system.',
        sectionIds: ['systems'],
        evidence:
          'Background links improved water supply and flushing toilets to increased sewage flows into the overloaded system. Verified in assigned source section: Background.',
        sourceSection: {
          title: 'Wikipedia: Background',
          url: 'https://en.wikipedia.org/wiki/Great_Stink#Background',
        },
      },
      {
        id: 'b4-great-stink-2',
        prompt: 'How did Snow’s explanation differ from the prevailing miasma theory?',
        options: [
          {
            id: '0',
            text: 'He emphasized contaminated water rather than inhaled bad air as the transmission route.',
          },
          {
            id: '1',
            text: 'He argued that cholera could not be associated with sanitation.',
          },
          {
            id: '2',
            text: 'He proposed that only factory smoke could transmit cholera.',
          },
          {
            id: '3',
            text: 'He accepted smell as the only necessary evidence of transmission.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '0',
        },
        explanation: 'Background contrasts the miasma belief with Snow’s waterborne explanation.',
        sectionIds: ['disease'],
        evidence:
          'Background contrasts the miasma belief with Snow’s waterborne explanation. Verified in assigned source section: Background.',
        sourceSection: {
          title: 'Wikipedia: Background',
          url: 'https://en.wikipedia.org/wiki/Great_Stink#Background',
        },
      },
      {
        id: 'b4-great-stink-3',
        prompt: 'Which description best matches the proposed sewer network?',
        options: [
          {
            id: '0',
            text: 'Independent household pits with no shared outlets',
          },
          {
            id: '1',
            text: 'A single pump that chemically disinfected every household’s drinking water',
          },
          {
            id: '2',
            text: 'Local sewers that continued discharging only beside each nearby house',
          },
          {
            id: '3',
            text: 'Smaller sewers feeding larger routes that carried sewage toward eastern outfalls',
          },
        ],
        answer: {
          kind: 'choice',
          value: '3',
        },
        explanation:
          'The plan collected local flows into larger intercepting routes, with pumps and eastward outfalls.',
        sectionIds: ['design'],
        evidence:
          'The plan collected local flows into larger intercepting routes, with pumps and eastward outfalls. Verified in assigned source section: Local_government.',
        sourceSection: {
          title: 'Wikipedia: Local government',
          url: 'https://en.wikipedia.org/wiki/Great_Stink#Local_government',
        },
      },
      {
        id: 'b4-great-stink-4',
        prompt: 'What did the legislation do that engineering drawings alone could not?',
        options: [
          {
            id: '0',
            text: 'Determine whether the Thames contained any water',
          },
          {
            id: '1',
            text: 'Assign responsibility and enable borrowing backed by a household levy',
          },
          {
            id: '2',
            text: 'Make the smell disappear immediately by declaring it illegal',
          },
          {
            id: '3',
            text: 'Establish the miasma theory as a laboratory result',
          },
        ],
        answer: {
          kind: 'choice',
          value: '1',
        },
        explanation:
          'The 1858 legislation placed responsibility with the MBW and authorized borrowing with a repayment levy.',
        sectionIds: ['politics'],
        evidence:
          'The 1858 legislation placed responsibility with the MBW and authorized borrowing with a repayment levy. Verified in assigned source section: June_to_August_1858.',
        sourceSection: {
          title: 'Wikipedia: June to August 1858',
          url: 'https://en.wikipedia.org/wiki/Great_Stink#June_to_August_1858',
        },
      },
      {
        id: 'b4-great-stink-5',
        prompt:
          'What is the best inference from sanitation improvements helping against cholera while many officials still believed in miasma?',
        options: [
          {
            id: '0',
            text: 'Success established that contaminated air was the only cause.',
          },
          {
            id: '1',
            text: 'The waterborne explanation was unnecessary to understanding the disease.',
          },
          {
            id: '2',
            text: 'A useful intervention can work for a mechanism different from the one its supporters believed.',
          },
          {
            id: '3',
            text: 'Improving drainage could have no relation to disease transmission.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '2',
        },
        explanation:
          'The assignment juxtaposes miasma-motivated action, waterborne evidence and beneficial sewage removal; the inference does not validate the incorrect theory.',
        sectionIds: ['disease'],
        evidence:
          'The assignment juxtaposes miasma-motivated action, waterborne evidence and beneficial sewage removal; the inference does not validate the incorrect theory. Verified in assigned source section: Background.',
        sourceSection: {
          title: 'Wikipedia: Background',
          url: 'https://en.wikipedia.org/wiki/Great_Stink#Background',
        },
      },
    ],
  },
];
